import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import DepartmentSelector from "./pages/DepartmentSelector";
import Audits from "./pages/Audits";
import Reports from "./pages/Reports";
import Stores from "./pages/Stores";
import Users from "./pages/Users";
import Feed from "./pages/Feed";
import Resources from "./pages/Resources";
import BestPractices from "./pages/BestPractices";
import ProfitLoss from "./pages/ProfitLoss";
import YearlySummary from "./pages/YearlySummary";
import DataUpload from "./pages/DataUpload";
import BusinessBrain from "./pages/BusinessBrain"; // legacy, kept for redirect compat
import TrainingTracker from "./pages/TrainingTracker";
import ServiceTickets from "./pages/ServiceTickets";
import QuickTasks from "./pages/QuickTasks";
import LandingPage from "./pages/LandingPage";
import Messaging from "./pages/Messaging";
import TrainingQuizzes from "./pages/TrainingQuizzes";
import AIAssistant from "./pages/AIAssistant";
import OpsHome from "./pages/OpsHome";
import AuditRun from "./pages/AuditRun";
import TemplateEditor, { TemplateList } from "./pages/TemplateEditor";
import ReportForm from "./pages/ReportForm";
import Inbox from "./pages/Inbox";
import ActionDetail, { ActionsList } from "./pages/Actions";
import OpsOverview from "./pages/OpsOverview";
import RestaurantPage from "./pages/RestaurantPage";
import OperatorReview, { ReviewsList } from "./pages/OperatorReview";
import LiveDashboard from "./pages/LiveDashboard";
import AuthCallback from "./pages/AuthCallback";
import Training from "./pages/Training";
import Compliance from "./pages/Compliance";
import SalesKPIs from "./pages/SalesKPIs";
import Financials from "./pages/Financials";
import WeeklyDashboard, { WeeklyDashboardDetail } from "./pages/WeeklyDashboard";
import InstallPrompt from "./components/InstallPrompt";

// Protected route with layout
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="text-center">
          <img
            src="https://customer-assets.emergentagent.com/job_resto-oversight/artifacts/215jxmio_Corp_ArrowLogo_Bear_2C_HL_EN_Lg.png"
            alt="A&W Logo"
            className="w-20 h-20 mx-auto mb-4 animate-pulse"
          />
          <div className="text-orange-600 font-medium">Loading...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Protected route without layout (for department selector)
const ProtectedRouteNoLayout = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="text-center">
          <img
            src="https://customer-assets.emergentagent.com/job_resto-oversight/artifacts/215jxmio_Corp_ArrowLogo_Bear_2C_HL_EN_Lg.png"
            alt="A&W Logo"
            className="w-20 h-20 mx-auto mb-4 animate-pulse"
          />
          <div className="text-orange-400 font-medium">Loading...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Role-based protected route
const RoleProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="text-center">
          <img
            src="https://customer-assets.emergentagent.com/job_resto-oversight/artifacts/215jxmio_Corp_ArrowLogo_Bear_2C_HL_EN_Lg.png"
            alt="A&W Logo"
            className="w-20 h-20 mx-auto mb-4 animate-pulse"
          />
          <div className="text-orange-600 font-medium">Loading...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!allowedRoles.includes(user.role)) {
    return <Navigate to="/select-department" replace />;
  }

  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="text-center">
          <img
            src="https://customer-assets.emergentagent.com/job_resto-oversight/artifacts/215jxmio_Corp_ArrowLogo_Bear_2C_HL_EN_Lg.png"
            alt="A&W Logo"
            className="w-20 h-20 mx-auto mb-4 animate-pulse"
          />
          <div className="text-orange-600 font-medium">Loading...</div>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to={homeFor(user)} replace />;
  }

  return children;
};

const homeFor = (user) => (['owner', 'area_manager', 'director'].includes(user?.role) ? '/overview' : '/home');
const HomeRedirect = () => { const { user } = useAuth(); return <Navigate to={homeFor(user)} replace />; };

function AppRoutes() {
  const location = useLocation();
  // Google sign-in return leg: process #session_id before any ProtectedRoute/PublicRoute logic runs.
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public Landing Page */}
      <Route path="/" element={<LandingPage />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={<ResetPassword />}
      />
      <Route
        path="/select-department"
        element={
          <ProtectedRoute>
            <DepartmentSelector />
          </ProtectedRoute>
        }
      />
      <Route path="/home" element={<ProtectedRoute><OpsHome /></ProtectedRoute>} />
      <Route path="/audits-v2/:id" element={<ProtectedRoute><AuditRun /></ProtectedRoute>} />
      <Route path="/templates" element={<RoleProtectedRoute allowedRoles={['owner', 'director', 'area_manager']}><TemplateList /></RoleProtectedRoute>} />
      <Route path="/templates/:id" element={<RoleProtectedRoute allowedRoles={['owner', 'director', 'area_manager']}><TemplateEditor /></RoleProtectedRoute>} />
      <Route path="/reports-v2/:id" element={<ProtectedRoute><ReportForm /></ProtectedRoute>} />
      <Route path="/inbox" element={<RoleProtectedRoute allowedRoles={['owner', 'director', 'area_manager']}><Inbox /></RoleProtectedRoute>} />
      <Route path="/actions" element={<ProtectedRoute><ActionsList /></ProtectedRoute>} />
      <Route path="/actions/:id" element={<ProtectedRoute><ActionDetail /></ProtectedRoute>} />
      <Route path="/overview" element={<RoleProtectedRoute allowedRoles={['owner', 'director', 'area_manager']}><OpsOverview /></RoleProtectedRoute>} />
      <Route path="/reviews" element={<ProtectedRoute><ReviewsList /></ProtectedRoute>} />
      <Route path="/reviews/:id" element={<ProtectedRoute><OperatorReview /></ProtectedRoute>} />
      <Route path="/training" element={<ProtectedRoute><Training /></ProtectedRoute>} />
      <Route path="/compliance" element={<ProtectedRoute><Compliance /></ProtectedRoute>} />
      <Route path="/sales" element={<ProtectedRoute><SalesKPIs /></ProtectedRoute>} />
      <Route path="/clearview-reports" element={<Navigate to="/sales?tab=reports" replace />} />
      <Route path="/weekly-dashboard" element={<ProtectedRoute><WeeklyDashboard /></ProtectedRoute>} />
      <Route path="/weekly-dashboard/:id" element={<ProtectedRoute><WeeklyDashboardDetail /></ProtectedRoute>} />
      <Route path="/restaurants" element={<Navigate to="/stores" replace />} />
      <Route path="/restaurants/:id" element={<ProtectedRoute><RestaurantPage /></ProtectedRoute>} />
      <Route
        path="/dashboard"
        element={
          <RoleProtectedRoute allowedRoles={['owner', 'director', 'area_manager', 'manager']}>
            <Dashboard />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/audits"
        element={
          <ProtectedRoute>
            <Audits />
          </ProtectedRoute>
        }
      />
      <Route
        path="/checklists"
        element={<Navigate to="/audits" replace />}
      />
      <Route
        path="/checklists/:id"
        element={<Navigate to="/audits" replace />}
      />
      <Route
        path="/reports"
        element={
          <RoleProtectedRoute allowedRoles={['owner', 'director', 'manager']}>
            <Reports />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/stores"
        element={
          <RoleProtectedRoute allowedRoles={['owner', 'director']}>
            <Stores />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <Users />
          </ProtectedRoute>
        }
      />
      <Route
        path="/feed"
        element={
          <ProtectedRoute>
            <Feed />
          </ProtectedRoute>
        }
      />
      <Route
        path="/resources"
        element={
          <ProtectedRoute>
            <Resources />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tips"
        element={<Navigate to="/best-practices" replace />}
      />
      <Route
        path="/best-practices"
        element={
          <ProtectedRoute>
            <BestPractices />
          </ProtectedRoute>
        }
      />
      <Route
        path="/kpi"
        element={<Navigate to="/sales?tab=kpis" replace />}
      />
      <Route
        path="/weekly-analysis"
        element={<Navigate to="/sales?tab=weekly" replace />}
      />
      <Route
        path="/consolidated-pl"
        element={<Navigate to="/financials?tab=pl" replace />}
      />
      <Route
        path="/financials"
        element={
          <RoleProtectedRoute allowedRoles={['owner', 'director', 'area_manager', 'manager']}>
            <Financials />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/profit-loss"
        element={
          <RoleProtectedRoute allowedRoles={['owner', 'director', 'manager']}>
            <ProfitLoss />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/yearly-summary"
        element={
          <RoleProtectedRoute allowedRoles={['owner', 'director', 'manager']}>
            <YearlySummary />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/data-upload"
        element={
          <RoleProtectedRoute allowedRoles={['owner', 'director', 'area_manager', 'manager']}>
            <DataUpload />
          </RoleProtectedRoute>
        }
      />
      <Route
        path="/financial-reports"
        element={<Navigate to="/financials?tab=reports" replace />}
      />
      <Route
        path="/service-times"
        element={<Navigate to="/sales?tab=service" replace />}
      />
      <Route
        path="/business-brain"
        element={<Navigate to="/best-practices" replace />}
      />
      <Route
        path="/training-tracker"
        element={
          <ProtectedRoute>
            <TrainingTracker />
          </ProtectedRoute>
        }
      />
      <Route
        path="/training-quizzes"
        element={
          <ProtectedRoute>
            <TrainingQuizzes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/service-tickets"
        element={
          <ProtectedRoute>
            <ServiceTickets />
          </ProtectedRoute>
        }
      />
      <Route
        path="/quick-tasks"
        element={
          <ProtectedRoute>
            <QuickTasks />
          </ProtectedRoute>
        }
      />
      <Route
        path="/live-dashboard"
        element={
          <Layout>
            <LiveDashboard />
          </Layout>
        }
      />
      <Route
        path="/messaging"
        element={
          <Layout>
            <Messaging />
          </Layout>
        }
      />
      <Route
        path="/ai-assistant"
        element={
          <Layout>
            <AIAssistant />
          </Layout>
        }
      />
      <Route path="/" element={<HomeRedirect />} />
      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
        <InstallPrompt />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
